import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppLayout } from "../shared/layout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getProfile, updateUser, getMyBadges, getTasks } from "../../../data";
import { GoogleAccountManagement } from "./google-account";
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User, Mail, Save, Trophy, Star, Flame, Target } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import AuthStore from "../../../stores/AuthStore";
import { PageLoader } from "@/components/shared/page-loader";

export function ProfileScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["profile"],
    queryFn: getProfile,
    onSuccess: (data) => {
      setFormData({
        name: data.data.user.name,
        email: data.data.user.email,
      });
    },
  });

  const { data: badgesResponse } = useQuery({
    queryKey: ["my-badges"],
    queryFn: getMyBadges,
  });

  const { data: tasksResponse } = useQuery({
    queryKey: ["tasks"],
    queryFn: getTasks,
  });

  const badges = badgesResponse?.data || [];
  const tasks = tasksResponse?.data || [];

  const updateProfileMutation = useMutation({
    mutationFn: (data) => updateUser(AuthStore.user?.id, data),
    onSuccess: (response) => {
      // Update AuthStore with new profile data
      if (response?.data) {
        AuthStore.setProfile(response.data);
      }
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      setIsEditing(false);
      toast({
        title: t("common.success"),
        description: t("profile.updateSuccess"),
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: t("common.error"),
        description: error.response?.data?.message || t("profile.updateFailed"),
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    updateProfileMutation.mutate({ name: formData.name });
  };

  if (isLoading) {
    return (
      <AppLayout title={t("profile.title")} description={t("profile.loading")}>
        <PageLoader />
      </AppLayout>
    );
  }
  
  if (error) return <AppLayout><div>{t("profile.loadError")}</div></AppLayout>;

  const user = data?.data?.user || data?.data || data;
  
  // Gamification Logic
  const xp = user?.xp || 0;
  const level = user?.level || 1;
  const currentLevelMinXp = Math.pow(level - 1, 2) * 100;
  const nextLevelMinXp = Math.pow(level, 2) * 100;
  const progress = Math.min(100, Math.max(0, ((xp - currentLevelMinXp) / (nextLevelMinXp - currentLevelMinXp)) * 100));

  // Streak and Completion Rate
  const streak = user?.streak ?? 0;
  const backendCompletionRate = user?.completionRate ?? null;
  const calculatedCompletionRate = tasks.length > 0 
    ? Math.round((tasks.filter(t => t.isCompleted).length / tasks.length) * 100) 
    : 0;
  const completionRate = backendCompletionRate !== null ? Math.round(backendCompletionRate) : calculatedCompletionRate;

  return (
    <AppLayout
      title={t("profile.title")}
      description={t("profile.subtitle")}
    >
      <div className="max-w-4xl space-y-8">
        {/* Profile Header / Avatar */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <Avatar className="h-24 w-24 border-4 border-white shadow-lg">
                <AvatarImage src={user?.avatar} />
                <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                  {user?.name?.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="text-center md:text-left space-y-1 flex-1">
                <h2 className="text-2xl font-bold">{user?.name}</h2>
                <p className="text-gray-500">{user?.email}</p>
                <div className="flex items-center justify-center md:justify-start gap-2 mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {user?.role?.name || "User"}
                  </span>
                  {user?.googleId && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {t("profile.googleConnected")}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Level & XP Card */}
              <div className="bg-muted p-4 rounded-xl w-full md:w-64">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-bold text-lg flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-yellow-500" />
                    {t("profile.level")} {level}
                  </span>
                  <span className="text-xs text-muted-foreground">{xp} XP</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-500" 
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-xs text-center mt-2 text-muted-foreground">
                  {Math.round(nextLevelMinXp - xp)} {t("profile.xpToLevel")} {level + 1}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <Flame className="h-6 w-6 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{streak}</p>
                  <p className="text-sm text-muted-foreground">{t("profile.dayStreak")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Target className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{completionRate}%</p>
                  <p className="text-sm text-muted-foreground">{t("profile.completionRate")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Star className="h-6 w-6 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{badges.length}</p>
                  <p className="text-sm text-muted-foreground">{t("profile.badgesEarned")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Badges Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-500" />
              {t("profile.badgesTitle")}
            </CardTitle>
            <CardDescription>{t("profile.badgesDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            {badges.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {badges.map((userBadge) => (
                  <div key={userBadge.id} className="flex flex-col items-center p-4 border rounded-lg bg-card hover:bg-accent transition-colors">
                    <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mb-2 text-2xl">
                      {userBadge.badge.icon || "🏅"}
                    </div>
                    <span className="font-medium text-sm text-center">{userBadge.badge.name}</span>
                    <span className="text-xs text-muted-foreground text-center mt-1">{userBadge.badge.description}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                {t("profile.noBadges")}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Personal Information */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t("profile.personalInfo")}</CardTitle>
                <CardDescription>{t("profile.personalInfoDesc")}</CardDescription>
              </div>
              {!isEditing && (
                <Button onClick={() => setIsEditing(true)} variant="outline">
                  {t("profile.editProfile")}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <div className="grid gap-6">
                <div className="grid gap-2">
                  <Label htmlFor="name">{t("profile.fullName")}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      disabled={!isEditing}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="email">{t("profile.emailAddress")}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      value={formData.email}
                      disabled={true} // Email usually shouldn't be editable directly without verification
                      className="pl-9 bg-gray-50"
                    />
                  </div>
                  <p className="text-xs text-gray-500">{t("profile.emailCannotChange")}</p>
                </div>
              </div>
              
              {isEditing && (
                <div className="flex justify-end gap-4 mt-6">
                  <Button 
                    type="button" 
                    variant="ghost" 
                    onClick={() => {
                      setIsEditing(false);
                      setFormData({ name: user.name, email: user.email });
                    }}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={updateProfileMutation.isLoading}>
                    <Save className="mr-2 h-4 w-4" />
                    {updateProfileMutation.isLoading ? t("common.saving") : t("common.saveChanges")}
                  </Button>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Connected Accounts */}
        {user?.googleId && (
          <GoogleAccountManagement />
        )}
      </div>
    </AppLayout>
  );
}
